import { useFormik } from "formik"
import { Button, CurrencyInput, Grid, Input, NumericInput, Switch, Table, Typography } from "../../../components"
import currency from 'currency.js'
import { formatMoney, nl2br } from "@bussola-viagens/utils"
import { useContext, useEffect, useMemo, useState } from "react"
import { PriceSetting } from '@bussola-viagens/constants'
import axios from 'axios'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { AuthContext } from "../../../contexts"
import { Alert, AlertTitle, Divider } from "@mui/material"
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid"
import { Delete } from "@mui/icons-material"
import { v4 } from "uuid"
import { useAPI } from "../../../hooks"

type Services = Array<{id: string, name: string, currency: string, amount: number}>

export const Repricing = () => {
    const [currencies, setCurrencies] = useState<Array<{value: string, label: string, rate: number}>>([])
    const [currencyRates] = useAPI(`/currency`)
    
    useEffect(() => {
        (async () => {
            const rates = Object.entries(currencyRates.data?.rates || {}).map(([key, value]) => {
                const rate = currency(1, {precision: 4}).divide(Number(value)).multiply(1.07).value

                return {
                    value: key,
                    label: `${key} (${formatMoney(rate)})`,
                    rate: Number(rate)
                }
            })

            setCurrencies([{ label: 'BRL', value: 'BRL', rate: 1 }, ...rates])
        })()
    }, [currencyRates])

    const formik = useFormik({
        initialValues: {
            services: [] as Services,
            repricingFee: 0,
            repricingFeeCurrency: 'BRL',
            partnerFee: 0,
            installmentInterest: 0,
            fareDiff: 0,
            fareDiffCurrency: 'BRL',
            serviceFee: 15,
            extraFee: 0,
        },
        onSubmit: () => {}
    })

    const servicesTotal = useMemo(() => {
        return formik.values.services.reduce((prev, curr) => {
            const rate = currencies.find(item => item.value === curr.currency)?.rate || 1

            const total = currency(curr.amount).multiply(rate)

            return currency(prev).add(total).value
        }, 0)
    }, [formik.values.services, currencies])

    const repriceFeeTotal = useMemo(() => {
        return currency(formik.values.repricingFee).multiply(currencies.find(item => item.value === formik.values.repricingFeeCurrency)?.rate || 1).value
    }, [formik.values.repricingFee, formik.values.repricingFeeCurrency, currencies])

    const fareDiffTotal = useMemo(() => {
        return currency(formik.values.fareDiff).multiply(currencies.find(item => item.value === formik.values.fareDiffCurrency)?.rate || 1).value
    }, [formik.values.fareDiff, formik.values.fareDiffCurrency, currencies])

    const serviceFeeTotal = useMemo(() => {
        return currency(currency(repriceFeeTotal).add(formik.values.partnerFee).add(fareDiffTotal)).multiply(currency(formik.values.serviceFee).divide(100)).value
    }, [repriceFeeTotal, formik.values.serviceFee, formik.values.partnerFee, fareDiffTotal])

    const repriceTotal = useMemo(() => {
        return currency(repriceFeeTotal).add(fareDiffTotal).add(formik.values.partnerFee).add(formik.values.installmentInterest).add(formik.values.extraFee).add(serviceFeeTotal).value
    }, [repriceFeeTotal, fareDiffTotal, formik.values.partnerFee, formik.values.installmentInterest, formik.values.extraFee, serviceFeeTotal])

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} justifyContent={"end"} display={"flex"} alignItems={"end"}>
                <Button
                    onClick={() => {
                        formik.setFieldValue('services', [
                            ...formik.values.services,
                            {id: v4(), name: '', currency: 'BRL', amount: 0}
                        ])
                    }}
                >
                    Adicionar Serviço
                </Button>
            </Grid>
            <Grid item xs={12}>
                <Table
                    columns={[
                        {
                            field: 'name',
                            type: 'string',
                            headerName: 'Serviço',
                            flex: 2,
                            renderCell: ({ row }) => {
                                return (
                                    <Input 
                                        variant="standard"
                                        name={`services[${row.index}].name`}
                                        value={formik.values.services[row.index].name}
                                        onChange={formik.handleChange}
                                    />
                                )
                            }
                        },
                        {
                            field: 'currency',
                            type: 'string',
                            headerName: 'Moeda',
                            flex: 1,
                            renderCell: ({row}) => {
                                return (
                                    <Input 
                                        variant="standard"
                                        name={`services[${row.index}].currency`}
                                        select
                                        options={currencies}                                        
                                        value={formik.values.services[row.index].currency}
                                        onChange={formik.handleChange}
                                    />
                                )
                            }
                        },
                        {
                            field: 'amount',
                            type: 'string',
                            headerName: 'Valor',
                            flex: 1,
                            renderCell: ({row}) => {
                                return (
                                    <CurrencyInput
                                        variant="standard"
                                        onChangeValue={(_, originalValue) => {
                                            formik.setFieldValue(`services[${row.index}].amount`, originalValue, true)
                                        }}
                                        value={formik.values.services[row.index].amount}
                                    />
                                )
                            }
                        },
                        {
                            field: 'total',
                            type: 'string',
                            headerName: 'Subtotal',
                            flex: 1,
                            valueGetter: (_, row) => {
                                console.log('row', row) 
                                const amount = Number(row.amount || 0)
                                const rowCurrency = currencies.find(item => item.value === row.currency)


                                return currency(amount).multiply(rowCurrency?.rate || 1).value
                            },
                            valueFormatter: (value) => {
                                return formatMoney(value)
                            }
                        },
                        {
                            field: 'actions',
                            type: 'actions',
                            getActions: (params: GridRowParams) => [
                                <GridActionsCellItem icon={<Delete />} onClick={() => {
                                    formik.setFieldValue('services', formik.values.services.filter((_, index) => index !== params.row.index))
                                }} label="Excluir" />,
                            ]
                            
                        }
                    ]}
                    rows={formik.values.services.map((service, index) => ({
                        ...service,
                        index
                    }))}
                    hideFooter
                />
            </Grid>
            <Grid item xs={12} marginTop={1} marginBottom={2}>
                <Grid container>
                    <Grid item xs={4}>
                        <Typography fontWeight="bold">
                            Total de Serviços
                        </Typography>
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography>
                            {formatMoney(servicesTotal)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Input 
                                    name={`repricingFeeCurrency`}
                                    select
                                    options={currencies}                                        
                                    value={formik.values.repricingFeeCurrency}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <CurrencyInput 
                                    label="Taxa de Remarcação"
                                    onChangeValue={(_, originalValue) => {
                                        formik.setFieldValue(`repricingFee`, originalValue, true)
                                    }}
                                    value={formik.values.repricingFee}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(repriceFeeTotal)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <CurrencyInput 
                            label="Valor pendente de pagamento (boleto) (R$)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue(`partnerFee`, originalValue, true)
                            }}
                            value={formik.values.partnerFee}
                        />
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(formik.values.partnerFee)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <CurrencyInput 
                            label="Juros de parcelamento (boleto/cartão) (R$)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue(`installmentInterest`, originalValue, true)
                            }}
                            value={formik.values.installmentInterest}
                        />
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(formik.values.installmentInterest)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <Input 
                                    name={`fareDiffCurrency`}
                                    select
                                    options={currencies}                                        
                                    value={formik.values.fareDiffCurrency}
                                    onChange={formik.handleChange}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <CurrencyInput 
                                    label="Diferença Tarifária"
                                    onChangeValue={(_, originalValue) => {
                                        formik.setFieldValue(`fareDiff`, originalValue, true)
                                    }}
                                    value={formik.values.fareDiff}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(fareDiffTotal)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <CurrencyInput 
                            label="Taxa de Serviço (%)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue(`serviceFee`, originalValue, true)
                            }}
                            value={formik.values.serviceFee}
                        />
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(serviceFeeTotal)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <CurrencyInput 
                            label="Valor Extra (R$)"
                            onChangeValue={(_, originalValue) => {
                                formik.setFieldValue(`extraFee`, originalValue, true)
                            }}
                            value={formik.values.extraFee}
                        />
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography color={"red"}>
                            -{formatMoney(formik.values.extraFee)}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Divider />
            </Grid>
            <Grid item xs={12} marginTop={1}>
                <Grid container>
                    <Grid item xs={4}>
                        <Typography fontWeight="bold">
                            Valor Total da Remarcação
                        </Typography>
                    </Grid>
                    <Grid item xs={8} justifyContent={"end"} display={"flex"} alignItems={"center"}>
                        <Typography>
                            {`${formatMoney(repriceFeeTotal)} + ${formatMoney(formik.values.partnerFee)} + ${formatMoney(formik.values.installmentInterest)} + ${formatMoney(fareDiffTotal)} + ${formatMoney(serviceFeeTotal)} + ${formatMoney(formik.values.extraFee)} = `}
                            <span style={{fontWeight: 'bold'}}>{formatMoney(repriceTotal)}</span>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}